import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Btn, Input, Select, Modal, SectionCard, Spinner } from '../../components/UI';
import { api } from '../../utils/api';

// HB Sales Tool W6 — market signals of the territory profile. The Business Partner keeps them
// (Administration also network-wide ones); everyone in the territory reads them. Every signal carries
// a source link; points are -30 … +30 and count while the signal is valid in the chosen period.

const th = { textAlign: 'left', padding: '8px 10px', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--muted)', borderBottom: '1px solid var(--line)', whiteSpace: 'nowrap' };
const td = { padding: '9px 10px', borderBottom: '1px solid var(--line)', verticalAlign: 'top', fontSize: '0.84rem' };
const host = (u) => String(u || '').replace(/^https?:\/\//, '').split('/')[0];
const day = (d) => (d ? String(d).slice(0, 10) : '');
const EMPTY = { name: '', kind: 'budget cycle', description: '', programmes: ['*'], sectors: [], criterion: '', points: 10, validFrom: '', validTo: '', sourceUrl: '', network: false };

export default function MarketSignals({ onChanged }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null);       // { id?, ...fields } while adding / editing

  const load = useCallback(() => { api.explore.getSignals().then(setD).catch(e => setError(e.message)); }, []);
  useEffect(() => { load(); }, [load]);
  const changed = () => { load(); onChanged && onChanged(); };

  async function remove(s) {
    if (!window.confirm(`Switch off "${s.name}"?`)) return;
    try { await api.explore.removeSignal(s.id); changed(); } catch (e) { setError(e.message); }
  }

  const progName = (slug) => (slug === '*' ? 'all programmes' : d?.programmes.find(p => p.slug === slug)?.name || slug);

  return (
    <SectionCard title="Market signals" action={d?.canEdit && <Btn size="sm" onClick={() => setForm({ ...EMPTY })}>+ Market signal</Btn>} style={{ marginTop: 14 }}>
      <p style={{ fontSize: '0.84rem', color: 'var(--muted)', margin: '0 0 10px', lineHeight: 1.5 }}>
        A signal adds or removes points for programmes (for chosen sectors or client criteria) while it is valid. In the heat map it shows as ◆. Every signal carries a link to its source.
      </p>
      {error && <Alert type="error">{error}</Alert>}
      {!d ? <Spinner /> : !d.signals.length ? (
        <p style={{ fontSize: '0.86rem', color: 'var(--muted)', margin: 0 }}>
          No market signals yet.{d.canEdit ? ' Add one — each with a source link.' : ' Your Business Partner adds them for the territory.'}
        </p>
      ) : (
        <div style={{ overflow: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr>{['', 'Signal', 'Kind', 'Programmes', 'Sectors / criterion', 'Points', 'Valid', 'Source', ''].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {d.signals.map(s => (
                <tr key={s.id} style={{ opacity: s.active ? 1 : 0.5 }}>
                  <td style={td} title={!s.active ? 'switched off' : s.inForce ? 'valid now' : 'outside today'}>{s.active && s.inForce ? '◆' : '◇'}</td>
                  <td style={td}><b>{s.name}</b>{!s.territory && <div style={{ fontSize: '0.72rem', color: 'var(--muted)' }}>network-wide</div>}{s.description && <div style={{ fontSize: '0.76rem', color: 'var(--muted)' }}>{s.description}</div>}</td>
                  <td style={td}>{s.kind}</td>
                  <td style={td}>{s.programmes.map(progName).join(', ')}</td>
                  <td style={td}>{[s.sectors.join(', '), s.criterion].filter(Boolean).join(' · ') || 'all'}</td>
                  <td style={{ ...td, fontWeight: 700, color: s.points < 0 ? 'var(--gate)' : 'var(--ink)' }}>{s.points > 0 ? `+${s.points}` : s.points}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{day(s.validFrom) || '…'} – {day(s.validTo) || '…'}</td>
                  <td style={td}><a href={s.sourceUrl} target="_blank" rel="noopener noreferrer">{host(s.sourceUrl)}</a></td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {d.canEdit && (d.isAdmin || s.territory) && s.active && <>
                      <Btn size="sm" variant="outline" onClick={() => setForm({ ...EMPTY, ...s, criterion: s.criterion || '', validFrom: day(s.validFrom), validTo: day(s.validTo) })}>Edit</Btn>{' '}
                      <Btn size="sm" variant="outline" onClick={() => remove(s)}>Switch off</Btn>
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {form && <SignalForm d={d} form={form} setForm={setForm} onClose={() => setForm(null)} onSaved={() => { setForm(null); changed(); }} />}
    </SectionCard>
  );
}

function SignalForm({ d, form, setForm, onClose, onSaved }) {
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const allProgs = form.programmes.includes('*');
  const toggleProg = (slug) => set('programmes', allProgs ? [slug] : form.programmes.includes(slug) ? form.programmes.filter(x => x !== slug) : [...form.programmes, slug]);

  async function save() {
    setBusy(true); setError(null);
    try {
      const body = { ...form, points: Number(form.points) };
      if (form.id) await api.explore.updateSignal(form.id, body); else await api.explore.addSignal(body);
      onSaved();
    } catch (e) { setError(e.message); setBusy(false); }
  }

  const lbl = { fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--muted)', margin: '10px 0 4px', display: 'block' };
  return (
    <Modal open onClose={onClose} width={680} title={form.id ? 'Edit market signal' : 'New market signal'}
      footer={<><Btn loading={busy} onClick={save}>Save</Btn><Btn variant="outline" onClick={onClose}>Cancel</Btn></>}>
      {error && <Alert type="error">{error}</Alert>}
      <label style={lbl}>Signal</label>
      <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Annual L&D budgets are set in Q4" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 10 }}>
        <div><label style={lbl}>Kind</label>
          <Select value={form.kind} onChange={e => set('kind', e.target.value)}>{d.kinds.map(k => <option key={k}>{k}</option>)}</Select></div>
        <div><label style={lbl}>Points (−30 … +30)</label>
          <Input type="number" min="-30" max="30" step="5" value={form.points} onChange={e => set('points', e.target.value)} /></div>
      </div>
      <label style={lbl}>Description</label>
      <Input value={form.description || ''} onChange={e => set('description', e.target.value)} />
      <label style={lbl}>Programmes</label>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <Chip on={allProgs} onClick={() => set('programmes', ['*'])}>All</Chip>
        {d.programmes.map(p => <Chip key={p.slug} on={!allProgs && form.programmes.includes(p.slug)} onClick={() => toggleProg(p.slug)}>{p.name}</Chip>)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div><label style={lbl}>Sector (optional)</label>
          <Select value={form.sectors[0] || ''} onChange={e => set('sectors', e.target.value ? [e.target.value] : [])}>
            <option value="">All sectors</option>{d.sectors.map(s => <option key={s}>{s}</option>)}</Select></div>
        <div><label style={lbl}>Client criterion (optional)</label>
          <Select value={form.criterion} onChange={e => set('criterion', e.target.value)}>
            <option value="">All clients</option>{d.criteria.map(c => <option key={c}>{c}</option>)}</Select></div>
        <div><label style={lbl}>Valid from</label><Input type="date" value={form.validFrom || ''} onChange={e => set('validFrom', e.target.value)} /></div>
        <div><label style={lbl}>Valid to</label><Input type="date" value={form.validTo || ''} onChange={e => set('validTo', e.target.value)} /></div>
      </div>
      <label style={lbl}>Source link (required)</label>
      <Input value={form.sourceUrl} onChange={e => set('sourceUrl', e.target.value)} placeholder="https://…" />
      {d.isAdmin && !form.id && (
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.86rem', marginTop: 12 }}>
          <input type="checkbox" checked={!!form.network} onChange={e => set('network', e.target.checked)} /> Network-wide (every territory)
        </label>
      )}
    </Modal>
  );
}

function Chip({ on, onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{ padding: '4px 11px', borderRadius: 999, fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer',
      border: '1px solid var(--line)', background: on ? 'var(--ink)' : '#fff', color: on ? '#fff' : 'var(--ink)' }}>{children}</button>
  );
}
